import React, { useState, useEffect, useCallback } from 'react';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { Mail, Clock, CheckCircle, AlertTriangle, XCircle, RefreshCw, Calendar, Send, Loader2, X, Building2, ChevronDown, Star, Bell, MessageSquare, Truck, AlertCircle, RotateCcw, Repeat, Edit3, Sparkles, TrendingUp, Zap, Timer, MessageCircle, Settings, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   color: 'bg-amber-50 text-amber-700 border-amber-200',       icon: Clock,          dot: 'bg-amber-400' },
  sent:      { label: 'Sent',      color: 'bg-blue-50 text-blue-700 border-blue-200',           icon: Send,           dot: 'bg-blue-400' },
  delivered: { label: 'Delivered', color: 'bg-emerald-50 text-emerald-700 border-emerald-200',  icon: Truck,          dot: 'bg-emerald-400' },
  replied:   { label: 'Replied',   color: 'bg-purple-50 text-purple-700 border-purple-200',     icon: Star,           dot: 'bg-purple-500' },
  failed:    { label: 'Failed',    color: 'bg-red-50 text-red-700 border-red-200',              icon: XCircle,        dot: 'bg-red-400' },
  bounced:   { label: 'Bounced',   color: 'bg-red-50 text-red-700 border-red-200',              icon: AlertTriangle,  dot: 'bg-red-400' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-50 text-gray-500 border-gray-200',           icon: X,              dot: 'bg-gray-400' },
  blocked_dns_pending: { label: 'Blocked (DNS Pending)', color: 'bg-orange-50 text-orange-700 border-orange-200', icon: Lock, dot: 'bg-orange-400' },
};

const TABS = [
  { id: 'attention',  label: 'Needs Attention', icon: AlertCircle, urgent: true },
  { id: 'follow_ups', label: 'Follow-Ups',     icon: Repeat },
  { id: 'insights',   label: 'Insights',        icon: TrendingUp, highlight: true },
  { id: 'pending',    label: 'Pending',         icon: Clock },
  { id: 'active',     label: 'Sent',            icon: Send },
  { id: 'replied',    label: 'Replied',         icon: Star, highlight: true },
  { id: 'failed',     label: 'Failed',          icon: AlertTriangle },
];

/* ─── Status Badge ─── */
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold border rounded-full ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
};

/* ─── Time Display ─── */
const TimeDisplay = ({ iso, tz, label }) => {
  if (!iso) return <span className="text-gray-400 text-[10px]">—</span>;
  const d = new Date(iso);
  return (
    <div className="text-xs">
      {label && <span className="text-[9px] text-gray-400 mr-1">{label}</span>}
      <span className="font-medium text-[#0A1F44]">{d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</span>
      <span className="text-gray-500 ml-1">{d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      {tz && <span className="text-[9px] text-gray-400 ml-1">({tz.replace('America/', '').replace('_', ' ')})</span>}
    </div>
  );
};

/* ─── Reschedule Modal ─── */
const RescheduleModal = ({ email, onClose, onDone }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const handleSubmit = async () => {
    if (!date) { toast.error('Select a date'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/pitch-email/scheduled/${email.schedule_id}/reschedule`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scheduled_for: `${date}T${time}:00`, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Rescheduled'); onDone(); } else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed to reschedule'); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 space-y-4" data-testid="reschedule-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#0A1F44]">Reschedule Email</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg text-xs">
          <span className="text-gray-500">To:</span> <span className="font-medium text-[#0A1F44]">{email.to_name || email.to_email}</span>
          <br /><span className="text-gray-500">Subject:</span> <span className="text-gray-700">{email.subject}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] text-gray-500 uppercase block mb-0.5">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} min={new Date().toISOString().split('T')[0]}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="reschedule-date" />
          </div>
          <div>
            <label className="text-[9px] text-gray-500 uppercase block mb-0.5">Time</label>
            <select value={time} onChange={e => setTime(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="reschedule-time">
              {['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <p className="text-[10px] text-gray-400">Business hours only (8 AM - 5 PM). Time in your local timezone.</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-1.5 text-xs font-bold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1" data-testid="reschedule-confirm">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Calendar className="h-3 w-3" />} Reschedule
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Edit Time Modal ─── */
const EditTimeModal = ({ email, onClose, onDone }) => {
  const currentDate = email.scheduled_for ? new Date(email.scheduled_for) : new Date();
  const [date, setDate] = useState(currentDate.toISOString().split('T')[0]);
  const [time, setTime] = useState((() => {
    const h = currentDate.getHours().toString().padStart(2, '0');
    const m = currentDate.getMinutes() < 30 ? '00' : '30';
    return `${h}:${m}`;
  })());
  const [saving, setSaving] = useState(false);
  const handleSubmit = async () => {
    if (!date) { toast.error('Select a date'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/pitch-email/scheduled/${email.schedule_id}/edit-time`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scheduled_for: `${date}T${time}:00`, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Time updated'); onDone(); } else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed to update time'); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 space-y-4" data-testid="edit-time-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#0A1F44]">Edit Send Time</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg text-xs">
          <span className="text-gray-500">To:</span> <span className="font-medium text-[#0A1F44]">{email.to_name || email.to_email}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] text-gray-500 uppercase block mb-0.5">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} min={new Date().toISOString().split('T')[0]}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="edit-date" />
          </div>
          <div>
            <label className="text-[9px] text-gray-500 uppercase block mb-0.5">Time</label>
            <select value={time} onChange={e => setTime(e.target.value)} className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="edit-time">
              {['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <p className="text-[10px] text-gray-400">Business hours only (8 AM - 5 PM).</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-1.5 text-xs font-bold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1" data-testid="edit-time-confirm">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Clock className="h-3 w-3" />} Update Time
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Reply Notes Modal ─── */
const ReplyModal = ({ email, onClose, onDone }) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const handleSubmit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/pitch-email/scheduled/${email.schedule_id}/mark-replied`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Marked as replied!'); onDone(); } else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed'); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 space-y-4" data-testid="reply-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#0A1F44] flex items-center gap-1.5"><Star className="h-4 w-4 text-purple-500" /> Mark as Replied</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg text-xs">
          <span className="text-gray-500">From:</span> <span className="font-bold text-purple-700">{email.to_name || email.to_email}</span>
          <span className="text-gray-400 ml-1">at {email.company}</span>
        </div>
        <div>
          <label className="text-[9px] text-gray-500 uppercase block mb-1">Reply Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="What did they say? Key takeaways, next steps..."
            rows={3} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg resize-y" data-testid="reply-notes" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-1.5 text-xs font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1" data-testid="reply-confirm">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Star className="h-3 w-3" />} Mark Replied
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Follow-Up Due Badge ─── */
const FollowUpBadge = ({ email }) => {
  if (!email.follow_up_due) return null;
  const days = email.days_since_sent || 0;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 rounded-full animate-pulse"
      data-testid={`follow-up-badge-${email.schedule_id}`}>
      <RotateCcw className="h-3 w-3" /> Follow-up due ({days}d)
    </span>
  );
};

/* ─── Email Row ─── */
const EmailRow = ({ email, onAction }) => {
  const [expanded, setExpanded] = useState(false);
  const status = email.status || 'pending';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const isReplied = status === 'replied';

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-shadow hover:shadow-sm ${
      isReplied ? 'border-purple-300 ring-1 ring-purple-100' :
      email.follow_up_due ? 'border-orange-300 ring-1 ring-orange-100' :
      status === 'failed' ? 'border-red-200' : 'border-gray-200'
    }`} data-testid={`email-row-${email.schedule_id}`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`h-2 w-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-xs font-bold truncate ${isReplied ? 'text-purple-700' : 'text-[#0A1F44]'}`}>{email.to_name || 'No name'}</span>
            <span className="text-[10px] text-gray-400 truncate">{email.to_email}</span>
            <FollowUpBadge email={email} />
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-[11px] text-gray-500 truncate">{email.company || '—'}</span>
            <span className="text-[10px] text-gray-400">|</span>
            <span className="text-[11px] text-gray-500 truncate">{email.subject}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {status === 'pending' && <TimeDisplay iso={email.scheduled_for} tz={email.timezone} />}
          {status === 'sent' && <TimeDisplay iso={email.sent_at} />}
          {status === 'delivered' && <TimeDisplay iso={email.delivered_at || email.sent_at} label="Delivered" />}
          {status === 'replied' && <TimeDisplay iso={email.replied_at} label="Replied" />}
          {status === 'failed' && <TimeDisplay iso={email.failed_at || email.created_at} />}
          <StatusBadge status={status} />
          {email.retried && <span className="text-[9px] text-blue-500 font-medium border border-blue-200 rounded px-1.5 py-0.5">Retried</span>}
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3 border-t border-gray-100 pt-2.5 space-y-2">
          <div className="grid grid-cols-4 gap-3 text-[10px]">
            <div><span className="text-gray-400 uppercase">Schedule ID</span><br /><span className="font-mono text-gray-600">{email.schedule_id}</span></div>
            <div><span className="text-gray-400 uppercase">Role</span><br /><span className="text-gray-600">{email.role_id || '—'}</span></div>
            <div><span className="text-gray-400 uppercase">Pitch Type</span><br /><span className="text-gray-600 capitalize">{email.pitch_type || '—'}</span></div>
            <div><span className="text-gray-400 uppercase">Timezone</span><br /><span className="text-gray-600">{email.timezone || '—'}</span></div>
          </div>

          {status === 'failed' && email.error && (
            <div className="p-2 bg-red-50 border border-red-100 rounded-lg">
              <span className="text-[10px] font-semibold text-red-600 uppercase">Failure Reason</span>
              <p className="text-[11px] text-red-700 mt-0.5">{email.error}</p>
            </div>
          )}

          {email.follow_up_due && status !== 'failed' && (
            <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg" data-testid={`follow-up-alert-${email.schedule_id}`}>
              <span className="text-[10px] font-semibold text-orange-700 uppercase">Follow-up due</span>
              <p className="text-[11px] text-orange-600 mt-0.5">
                Sent {email.days_since_sent} day{email.days_since_sent !== 1 ? 's' : ''} ago — no reply yet. Most deals happen on the 2nd or 3rd touch.
              </p>
            </div>
          )}

          {status === 'replied' && email.reply_notes && (
            <div className="p-2 bg-purple-50 border border-purple-100 rounded-lg">
              <span className="text-[10px] font-semibold text-purple-600 uppercase">Reply Notes</span>
              <p className="text-[11px] text-purple-700 mt-0.5">{email.reply_notes}</p>
            </div>
          )}

          {email.rescheduled_at && (
            <div className="text-[10px] text-blue-600">Rescheduled at {new Date(email.rescheduled_at).toLocaleString()}</div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {status === 'failed' && (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-lg">
                  <Lock className="h-3 w-3" /> Resend disabled — domain verification pending
                </span>
              </div>
            )}
            {status === 'pending' && (
              <>
                <button onClick={() => onAction('edit-time', email)}
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50"
                  data-testid={`edit-time-btn-${email.schedule_id}`}>
                  <Clock className="h-3 w-3" /> Edit Time
                </button>
                <button onClick={() => onAction('cancel', email)}
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                  data-testid={`cancel-btn-${email.schedule_id}`}>
                  <X className="h-3 w-3" /> Cancel
                </button>
              </>
            )}
            {(status === 'sent' || status === 'delivered') && (
              <>
                <button onClick={() => onAction('mark-replied', email)}
                  className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  data-testid={`mark-replied-btn-${email.schedule_id}`}>
                  <Star className="h-3 w-3" /> Mark as Replied
                </button>
                {email.follow_up_due && (
                  <button onClick={() => onAction('follow-up', email)}
                    className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                    data-testid={`follow-up-btn-${email.schedule_id}`}>
                    <RotateCcw className="h-3 w-3" /> Send Follow-Up
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Follow-Up Edit Modal ─── */
const EditFollowUpModal = ({ sequence, step, onClose, onDone }) => {
  const [subject, setSubject] = useState(step.subject || '');
  const [body, setBody] = useState(step.body_html || '');
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/follow-ups/${sequence.sequence_id}/edit/${step.step}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject, body_html: body }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Follow-up updated'); onDone(); } else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/follow-ups/${sequence.sequence_id}/regenerate/${step.step}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setSubject(data.subject);
        setBody(data.body_html);
        toast.success('Message regenerated');
      } else toast.error(data.detail || 'Failed');
    } catch { toast.error('Regeneration failed'); }
    setRegenerating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-4" data-testid="edit-followup-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#0A1F44]">Edit Follow-Up</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="h-4 w-4 text-gray-400" /></button>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg text-xs">
          <span className="text-gray-500">To:</span> <span className="font-medium">{sequence.to_name}</span>
          <span className="text-gray-400 ml-1">at {sequence.company}</span>
        </div>
        <div>
          <label className="text-[9px] text-gray-500 uppercase block mb-0.5">Subject</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" data-testid="edit-fu-subject" />
        </div>
        <div>
          <label className="text-[9px] text-gray-500 uppercase block mb-0.5">Message</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={5}
            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg resize-y font-mono" data-testid="edit-fu-body" />
        </div>
        <div className="flex items-center justify-between">
          <button onClick={handleRegenerate} disabled={regenerating}
            className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            data-testid="regenerate-fu">
            {regenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Regenerate AI
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 text-xs font-bold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1"
              data-testid="save-fu">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Edit3 className="h-3 w-3" />} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Follow-Up Sequence Card ─── */
const SequenceCard = ({ sequence, onRefresh }) => {
  const [expanded, setExpanded] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [approving, setApproving] = useState(null);

  const isActive = sequence.status === 'active';
  const isCancelled = sequence.status === 'cancelled';

  const handleApprove = async (stepNum) => {
    setApproving(stepNum);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/follow-ups/${sequence.sequence_id}/approve/${stepNum}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) toast.success(`Follow-up approved — scheduled for ${new Date(data.scheduled_for).toLocaleDateString()}`);
      else toast.error(data.detail || 'Failed');
    } catch { toast.error('Approval failed'); }
    setApproving(null);
    onRefresh();
  };

  const handleCancel = async () => {
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/follow-ups/${sequence.sequence_id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) toast.success('Sequence cancelled'); else toast.error(data.detail || 'Failed');
    } catch { toast.error('Cancel failed'); }
    onRefresh();
  };

  const STEP_STATUS = {
    pending_approval: { label: 'Awaiting Approval', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
    approved:         { label: 'Approved',          color: 'bg-blue-100 text-blue-700 border-blue-300',     icon: CheckCircle },
    sent:             { label: 'Sent',              color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: Send },
    cancelled:        { label: 'Cancelled',         color: 'bg-gray-100 text-gray-500 border-gray-300',     icon: X },
  };

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-shadow hover:shadow-sm ${
      isCancelled ? 'border-gray-200 opacity-60' : 'border-blue-200'
    }`} data-testid={`sequence-${sequence.sequence_id}`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <Repeat className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-bold text-[#0A1F44]">{sequence.to_name}</span>
            <span className="text-[10px] text-gray-400">{sequence.to_email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="h-3 w-3 text-gray-400" />
            <span className="text-[11px] text-gray-500">{sequence.company}</span>
            <span className="text-[10px] text-gray-400">|</span>
            <span className="text-[11px] text-gray-500">{sequence.role_title || sequence.original_subject}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {sequence.steps.map(step => {
            const cfg = STEP_STATUS[step.status] || STEP_STATUS.pending_approval;
            return (
              <span key={step.step} className={`inline-flex items-center gap-0.5 px-2 py-0.5 text-[9px] font-semibold border rounded-full ${cfg.color}`}>
                <cfg.icon className="h-2.5 w-2.5" /> Step {step.step}: {cfg.label}
              </span>
            );
          })}
          {isCancelled && (
            <span className="text-[9px] text-gray-400 font-medium">{sequence.cancel_reason === 'recipient_replied' ? 'Cancelled (replied)' : 'Cancelled'}</span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3 border-t border-gray-100 pt-2.5 space-y-3">
          {sequence.steps.map((step) => {
            const cfg = STEP_STATUS[step.status] || STEP_STATUS.pending_approval;
            return (
              <div key={step.step} className="p-3 bg-gray-50 rounded-lg space-y-2" data-testid={`step-${sequence.sequence_id}-${step.step}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-[#0A1F44] uppercase">Follow-Up #{step.step}</span>
                  <div className="flex items-center gap-2">
                    <TimeDisplay iso={step.scheduled_for} tz={sequence.timezone} label="Scheduled" />
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-semibold border rounded-full ${cfg.color}`}>
                      {cfg.label}
                    </span>
                  </div>
                </div>

                {/* Message preview */}
                <div className="p-2 bg-white rounded border border-gray-200">
                  <div className="text-[10px] text-gray-500 mb-1">Subject: <span className="text-[#0A1F44] font-medium">{step.subject}</span></div>
                  <div className="text-[11px] text-gray-600" dangerouslySetInnerHTML={{ __html: step.body_html }} />
                </div>

                {/* Actions */}
                {step.status === 'pending_approval' && isActive && (
                  <div className="flex items-center gap-2">
                    <button disabled title="Domain verification required — Resend sending is disabled"
                      className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed opacity-70"
                      data-testid={`approve-${sequence.sequence_id}-${step.step}`}>
                      <Lock className="h-3 w-3" /> Sending Disabled
                    </button>
                    <button onClick={() => setEditModal(step)}
                      className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium border border-gray-200 rounded-lg hover:bg-gray-50"
                      data-testid={`edit-${sequence.sequence_id}-${step.step}`}>
                      <Edit3 className="h-3 w-3" /> Edit
                    </button>
                  </div>
                )}
                {step.status === 'approved' && (
                  <div className="text-[10px] text-blue-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Approved — in send queue
                    {step.schedule_id && <span className="text-gray-400 ml-1">({step.schedule_id})</span>}
                  </div>
                )}
              </div>
            );
          })}

          {/* Cancel sequence */}
          {isActive && (
            <button onClick={handleCancel}
              className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              data-testid={`cancel-seq-${sequence.sequence_id}`}>
              <X className="h-3 w-3" /> Cancel Sequence
            </button>
          )}
        </div>
      )}

      {editModal && (
        <EditFollowUpModal sequence={sequence} step={editModal} onClose={() => setEditModal(null)}
          onDone={() => { setEditModal(null); onRefresh(); }} />
      )}
    </div>
  );
};

/* ─── Insights Tab ─── */
const INSIGHT_ICONS = {
  metric: TrendingUp, winner: Zap, insight: MessageCircle,
  tip: Star, timing: Timer, success: CheckCircle, info: Mail,
};
const INSIGHT_STYLES = {
  metric:  'bg-blue-50 border-blue-200 text-blue-800',
  winner:  'bg-emerald-50 border-emerald-200 text-emerald-800',
  insight: 'bg-purple-50 border-purple-200 text-purple-800',
  tip:     'bg-amber-50 border-amber-200 text-amber-800',
  timing:  'bg-cyan-50 border-cyan-200 text-cyan-800',
  success: 'bg-green-50 border-green-200 text-green-800',
  info:    'bg-gray-50 border-gray-200 text-gray-600',
};

const InsightsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/follow-ups/analytics`);
        setData(res);
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, []);

  const handleModeChange = async (newMode) => {
    if (newMode === 'auto') return; // Coming soon
    setSavingMode(true);
    try {
      await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/follow-ups/timing-config`, {
        method: 'PUT', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ mode: newMode }),
      });
      setData(prev => ({...prev, timing_optimization: {...prev.timing_optimization, mode: newMode}}));
    } catch { /* silent */ }
    setSavingMode(false);
  };

  const handleApplySuggestion = async (d1, d2) => {
    setSavingMode(true);
    try {
      await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/follow-ups/timing-config`, {
        method: 'PUT', headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({ current_delay_days_1: d1, current_delay_days_2: d2 }),
      });
      // Reload analytics
      const res = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/follow-ups/analytics`);
      setData(res);
    } catch { /* silent */ }
    setSavingMode(false);
  };

  if (loading) return <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading insights...</div>;
  if (!data) return <div className="text-center py-16 text-gray-400 text-sm">Failed to load analytics</div>;

  const { summary, reply_by_step, time_to_reply, message_performance, insights, timing_optimization, optimal_send_time } = data;
  const hasData = summary.total_emails_sent > 0;

  return (
    <div className="space-y-5" data-testid="insights-tab">
      {/* ── Smart Insights ── */}
      <div className="space-y-2" data-testid="smart-insights">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-[#D4AF37]" /> Smart Takeaways
          </h3>
          {hasData && (
            <span className="text-[10px] text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full" data-testid="sample-size-label">
              Sample size: {summary.total_emails_sent} {summary.total_emails_sent === 1 ? 'email' : 'emails'}
            </span>
          )}
        </div>
        {insights.map((insight, i) => {
          const Icon = INSIGHT_ICONS[insight.type] || TrendingUp;
          const style = INSIGHT_STYLES[insight.type] || INSIGHT_STYLES.info;
          return (
            <div key={i} className={`flex items-start gap-2.5 px-4 py-2.5 border rounded-xl text-xs leading-relaxed ${style}`} data-testid={`insight-${i}`}>
              <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 opacity-70" />
              <span className="font-medium">{insight.text}</span>
            </div>
          );
        })}
      </div>

      {!hasData ? (
        <div className="text-center py-8">
          <Mail className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No email data yet</p>
          <p className="text-[10px] text-gray-300 mt-1">Send pitch emails to start seeing analytics</p>
        </div>
      ) : (
        <>
          {/* ── Reply Rate by Step ── */}
          <div data-testid="reply-by-step">
            <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5 text-purple-500" /> Reply Rate by Step
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {reply_by_step.map(step => (
                <div key={step.step} className={`p-4 rounded-xl border transition-shadow hover:shadow-sm ${
                  step.reply_rate > 0 ? 'bg-white border-purple-200 ring-1 ring-purple-50' : 'bg-white border-gray-200'
                }`} data-testid={`step-rate-${step.step}`}>
                  <div className="text-[10px] text-gray-500 font-medium mb-1">{step.display_name}</div>
                  <div className={`text-2xl font-black ${step.reply_rate > 0 ? 'text-purple-700' : 'text-gray-300'}`}>
                    {step.reply_rate}%
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {step.replied} {step.replied === 1 ? 'reply' : 'replies'} / {step.sent} sent
                  </div>
                  {/* Mini bar */}
                  <div className="h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${Math.min(step.reply_rate, 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Time to Reply + Message Performance ── */}
          <div className="grid grid-cols-2 gap-4">
            {/* Time to Reply */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="time-to-reply">
              <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Timer className="h-3.5 w-3.5 text-cyan-500" /> Time to Reply
              </h3>
              {time_to_reply.overall_avg_hours !== null ? (
                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] text-gray-500 mb-0.5">Overall Average</div>
                    <div className="text-xl font-black text-[#0A1F44]">
                      {time_to_reply.overall_avg_hours < 24
                        ? `${time_to_reply.overall_avg_hours}h`
                        : `${(time_to_reply.overall_avg_hours / 24).toFixed(1)}d`}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2.5 bg-gray-50 rounded-lg">
                      <div className="text-[9px] text-gray-400 uppercase">Initial Emails</div>
                      <div className="text-sm font-bold text-[#0A1F44]">
                        {time_to_reply.initial_avg_hours !== null
                          ? (time_to_reply.initial_avg_hours < 24 ? `${time_to_reply.initial_avg_hours}h` : `${(time_to_reply.initial_avg_hours / 24).toFixed(1)}d`)
                          : '—'}
                      </div>
                      <div className="text-[9px] text-gray-400">{time_to_reply.initial_count} replies</div>
                    </div>
                    <div className="p-2.5 bg-gray-50 rounded-lg">
                      <div className="text-[9px] text-gray-400 uppercase">Follow-Ups</div>
                      <div className="text-sm font-bold text-[#0A1F44]">
                        {time_to_reply.follow_up_avg_hours !== null
                          ? (time_to_reply.follow_up_avg_hours < 24 ? `${time_to_reply.follow_up_avg_hours}h` : `${(time_to_reply.follow_up_avg_hours / 24).toFixed(1)}d`)
                          : '—'}
                      </div>
                      <div className="text-[9px] text-gray-400">{time_to_reply.follow_up_count} replies</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Timer className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                  <p className="text-[11px] text-gray-400">No reply data yet</p>
                </div>
              )}
            </div>

            {/* Message Performance */}
            <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="message-performance">
              <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <MessageCircle className="h-3.5 w-3.5 text-purple-500" /> Message Performance
              </h3>
              {(message_performance.short.sent > 0 || message_performance.detailed.sent > 0) ? (
                <div className="space-y-3">
                  {[
                    { key: 'short', label: 'Short Messages', desc: '< 400 chars' },
                    { key: 'detailed', label: 'Detailed Messages', desc: '400+ chars' },
                  ].map(type => {
                    const d = message_performance[type.key];
                    const isWinner = d.reply_rate > 0 && d.reply_rate >= (message_performance[type.key === 'short' ? 'detailed' : 'short'].reply_rate || 0);
                    return (
                      <div key={type.key} className={`p-3 rounded-lg border ${
                        isWinner && d.sent > 0 ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'
                      }`} data-testid={`perf-${type.key}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <span className="text-[11px] font-bold text-[#0A1F44]">{type.label}</span>
                            <span className="text-[9px] text-gray-400 ml-1">({type.desc})</span>
                          </div>
                          {isWinner && d.sent > 2 && (
                            <span className="text-[9px] font-bold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded-full">TOP</span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-3">
                          <span className={`text-lg font-black ${d.reply_rate > 0 ? 'text-purple-700' : 'text-gray-300'}`}>
                            {d.reply_rate}%
                          </span>
                          <span className="text-[10px] text-gray-400">{d.replied}/{d.sent} replied</span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Top replied messages */}
                  {message_performance.top_replied_messages?.length > 0 && (
                    <div>
                      <div className="text-[9px] text-gray-500 uppercase font-medium mb-1.5">Messages That Got Replies</div>
                      {message_performance.top_replied_messages.map((msg, i) => (
                        <div key={i} className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0">
                          <Star className="h-3 w-3 text-purple-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-[11px] text-[#0A1F44] font-medium truncate">{msg.subject}</div>
                            <div className="text-[9px] text-gray-400">{msg.to_name} at {msg.company} · {msg.message_length} · {msg.is_follow_up ? 'follow-up' : 'initial'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <MessageCircle className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                  <p className="text-[11px] text-gray-400">No message data yet</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Summary Bar ── */}
          <div className="flex items-center justify-center gap-6 py-3 px-4 bg-gray-50 border border-gray-200 rounded-xl" data-testid="analytics-summary">
            {[
              { label: 'Emails Sent', val: summary.total_emails_sent, color: 'text-blue-600' },
              { label: 'Total Replies', val: summary.total_replies, color: 'text-purple-700' },
              { label: 'Reply Rate', val: `${summary.overall_reply_rate}%`, color: 'text-purple-700' },
              { label: 'Active Sequences', val: summary.active_sequences, color: 'text-emerald-600' },
              { label: 'Total Sequences', val: summary.total_sequences, color: 'text-gray-600' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className={`text-sm font-black ${s.color}`}>{s.val}</div>
                <div className="text-[9px] text-gray-400 font-medium">{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Timing Optimization ── */}
          {timing_optimization && (
            <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="timing-optimization">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider flex items-center gap-1.5">
                  <Settings className="h-3.5 w-3.5 text-gray-500" /> Timing Optimization
                </h3>
                {/* Mode Toggle */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5" data-testid="timing-mode-toggle">
                  {[
                    { mode: 'manual', label: 'Manual' },
                    { mode: 'suggested', label: 'Suggested' },
                    { mode: 'auto', label: 'Auto', disabled: true },
                  ].map(m => (
                    <button
                      key={m.mode}
                      onClick={() => !m.disabled && handleModeChange(m.mode)}
                      disabled={savingMode || m.disabled}
                      className={`text-[10px] font-medium px-2.5 py-1 rounded-md transition-all flex items-center gap-1 ${
                        timing_optimization.mode === m.mode
                          ? 'bg-white text-[#0A1F44] shadow-sm'
                          : m.disabled
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:text-gray-700 cursor-pointer'
                      }`}
                      data-testid={`timing-mode-${m.mode}`}
                    >
                      {m.disabled && <Lock className="h-2.5 w-2.5" />}
                      {m.label}
                      {m.disabled && <span className="text-[8px] text-gray-300 ml-0.5">Soon</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Threshold Progress */}
              {!timing_optimization.threshold_met && (
                <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="threshold-progress">
                  <div className="text-[11px] text-gray-500 font-medium mb-2">Collecting data for optimization...</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400">Emails</span>
                        <span className="text-[10px] font-bold text-[#0A1F44]">{timing_optimization.threshold_progress.emails_sent} / {timing_optimization.threshold_progress.emails_needed}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min((timing_optimization.threshold_progress.emails_sent / timing_optimization.threshold_progress.emails_needed) * 100, 100)}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400">Replies</span>
                        <span className="text-[10px] font-bold text-[#0A1F44]">{timing_optimization.threshold_progress.replies_received} / {timing_optimization.threshold_progress.replies_needed}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${Math.min((timing_optimization.threshold_progress.replies_received / timing_optimization.threshold_progress.replies_needed) * 100, 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Timing */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-[9px] text-gray-400 uppercase font-medium mb-0.5">Follow-Up #1</div>
                  <div className="text-sm font-black text-[#0A1F44]">Day {timing_optimization.current_timing.follow_up_1_day}</div>
                  <div className="text-[9px] text-gray-400">after initial email</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-[9px] text-gray-400 uppercase font-medium mb-0.5">Follow-Up #2</div>
                  <div className="text-sm font-black text-[#0A1F44]">Day {timing_optimization.current_timing.follow_up_2_day}</div>
                  <div className="text-[9px] text-gray-400">after initial email</div>
                </div>
              </div>

              {/* Suggestion (only when threshold met and suggestion exists) */}
              {timing_optimization.threshold_met && timing_optimization.suggested_timing ? (
                <div className="p-3 border border-purple-200 bg-purple-50 rounded-lg" data-testid="timing-suggestion">
                  <div className="flex items-start gap-2 mb-2">
                    <Zap className="h-3.5 w-3.5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[11px] font-bold text-purple-800">Suggested Timing Adjustment</div>
                      <div className="text-[10px] text-purple-600 mt-0.5">{timing_optimization.suggested_timing.reason}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="p-2 bg-white rounded border border-purple-200">
                      <div className="text-[9px] text-gray-400 uppercase">Suggested #1</div>
                      <div className="text-sm font-black text-purple-700">Day {timing_optimization.suggested_timing.follow_up_1_day}</div>
                    </div>
                    <div className="p-2 bg-white rounded border border-purple-200">
                      <div className="text-[9px] text-gray-400 uppercase">Suggested #2</div>
                      <div className="text-sm font-black text-purple-700">Day {timing_optimization.suggested_timing.follow_up_2_day}</div>
                    </div>
                  </div>
                  {timing_optimization.mode === 'suggested' && (
                    <button
                      onClick={() => handleApplySuggestion(timing_optimization.suggested_timing.follow_up_1_day, timing_optimization.suggested_timing.follow_up_2_day)}
                      disabled={savingMode}
                      className="w-full text-[10px] font-bold text-white bg-purple-600 hover:bg-purple-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                      data-testid="apply-suggestion-btn"
                    >
                      {savingMode ? 'Applying...' : 'Apply Suggested Timing'}
                    </button>
                  )}
                  {timing_optimization.mode === 'manual' && (
                    <div className="text-[9px] text-purple-500 text-center">Switch to "Suggested" mode to apply this recommendation</div>
                  )}
                </div>
              ) : timing_optimization.threshold_met ? (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center" data-testid="timing-no-suggestion">
                  <CheckCircle className="h-4 w-4 text-green-500 mx-auto mb-1" />
                  <div className="text-[11px] text-green-700 font-medium">Current timing looks optimal</div>
                  <div className="text-[9px] text-green-500">No adjustments suggested based on your data</div>
                </div>
              ) : null}
            </div>
          )}

          {/* ── Optimal Send Time ── */}
          {optimal_send_time && (
            <div className="p-4 bg-white border border-gray-200 rounded-xl" data-testid="optimal-send-time">
              <h3 className="text-xs font-bold text-[#0A1F44] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-blue-500" /> Optimal Send Time
              </h3>

              {/* Threshold Progress */}
              {!optimal_send_time.threshold_met && (
                <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="send-time-threshold">
                  <div className="text-[11px] text-gray-500 font-medium mb-2">Collecting send-time data...</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400">Emails</span>
                        <span className="text-[10px] font-bold text-[#0A1F44]">{optimal_send_time.threshold_progress.emails_sent} / {optimal_send_time.threshold_progress.emails_needed}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min((optimal_send_time.threshold_progress.emails_sent / optimal_send_time.threshold_progress.emails_needed) * 100, 100)}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400">Replies</span>
                        <span className="text-[10px] font-bold text-[#0A1F44]">{optimal_send_time.threshold_progress.replies_received} / {optimal_send_time.threshold_progress.replies_needed}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${Math.min((optimal_send_time.threshold_progress.replies_received / optimal_send_time.threshold_progress.replies_needed) * 100, 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Time Block Grid */}
              {(() => {
                const blocks = optimal_send_time.time_blocks || [];
                const maxSent = Math.max(...blocks.map(b => b.sent), 1);
                const hasAnyData = blocks.some(b => b.sent > 0);
                const bestBlock = optimal_send_time.suggestion?.suggested_window;

                return hasAnyData ? (
                  <div className="space-y-1.5 mb-3" data-testid="send-time-blocks">
                    {blocks.filter(b => b.sent > 0 || b.block === '8-10 AM' || b.block === '10-12 PM' || b.block === '2-4 PM').map(block => (
                      <div key={block.block} className={`flex items-center gap-3 p-2 rounded-lg border transition-shadow ${
                        block.block === bestBlock ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-100' : 'bg-white border-gray-100'
                      }`} data-testid={`time-block-${block.start_hour}`}>
                        <div className="w-16 text-[10px] font-bold text-[#0A1F44] flex-shrink-0">{block.block}</div>
                        <div className="flex-1">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${
                              block.block === bestBlock ? 'bg-blue-500' : block.replied > 0 ? 'bg-purple-400' : 'bg-gray-300'
                            }`} style={{ width: `${(block.sent / maxSent) * 100}%` }} />
                          </div>
                        </div>
                        <div className="w-12 text-right">
                          <span className={`text-xs font-black ${block.reply_rate > 0 ? 'text-purple-700' : 'text-gray-300'}`}>{block.reply_rate}%</span>
                        </div>
                        <div className="w-16 text-right text-[9px] text-gray-400">{block.sent} sent</div>
                        {block.block === bestBlock && (
                          <span className="text-[8px] font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full flex-shrink-0">BEST</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 mb-3">
                    <Clock className="h-6 w-6 text-gray-300 mx-auto mb-1" />
                    <p className="text-[11px] text-gray-400">No send-time data yet</p>
                  </div>
                );
              })()}

              {/* Suggestion */}
              {optimal_send_time.threshold_met && optimal_send_time.suggestion ? (
                <div className="p-3 border border-blue-200 bg-blue-50 rounded-lg" data-testid="send-time-suggestion">
                  <div className="flex items-start gap-2">
                    <Zap className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[11px] font-bold text-blue-800">
                        Suggested send window: {optimal_send_time.suggestion.suggested_window}
                      </div>
                      <div className="text-[10px] text-blue-600 mt-0.5">{optimal_send_time.suggestion.reason}</div>
                      <div className="text-[9px] text-blue-400 mt-1">Use this when scheduling — you can always override manually</div>
                    </div>
                  </div>
                </div>
              ) : optimal_send_time.threshold_met ? (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center" data-testid="send-time-no-suggestion">
                  <CheckCircle className="h-4 w-4 text-green-500 mx-auto mb-1" />
                  <div className="text-[11px] text-green-700 font-medium">No clear send-time pattern yet</div>
                  <div className="text-[9px] text-green-500">Keep sending — patterns emerge with more data</div>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
};



/* ─── Main Dashboard ─── */
const ScheduledEmailQueue = ({ embedded = false }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [emails, setEmails] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [stats, setStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [fuCounts, setFuCounts] = useState({});

  // Map tab IDs to query statuses
  const tabStatusMap = {
    attention: 'needs_attention',
    pending: 'pending',
    active: null, // will query sent + delivered
    replied: 'replied',
    failed: 'failed',
  };

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = tabStatusMap[activeTab];
      let url = `${API_URL}/api/intelligence/acquisition/pitch-email/queue`;
      if (statusParam) url += `?status=${statusParam}`;

      const data = await fetchWithAuth(url);
      let filteredEmails = data.emails || [];

      // For "active" tab, show sent + delivered
      if (activeTab === 'active') {
        filteredEmails = filteredEmails.filter(e => e.status === 'sent' || e.status === 'delivered');
      }

      setEmails(filteredEmails);
      setCounts(data.counts || {});
    } catch {
      toast.error('Failed to load email queue');
    }
    setLoading(false);
  }, [activeTab]);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pitch-email/notifications`);
      setNotifications(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pitch-email/delivery-stats`);
      setStats(data);
    } catch { /* silent */ }
  }, []);

  const loadFollowUps = useCallback(async () => {
    try {
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/follow-ups`);
      setFollowUps(data.sequences || []);
      setFuCounts(data.counts || {});
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => { loadNotifications(); loadStats(); loadFollowUps(); }, [loadNotifications, loadStats, loadFollowUps]);

  const handleAction = async (action, email) => {
    if (action === 'reschedule') { setModal({ type: 'reschedule', email }); return; }
    if (action === 'edit-time') { setModal({ type: 'edit-time', email }); return; }
    if (action === 'mark-replied') { setModal({ type: 'reply', email }); return; }
    if (action === 'follow-up') {
      // Open the pitch email modal for this role (navigate to acquisition pipeline)
      toast.info(`Follow-up needed for ${email.to_name || email.to_email} at ${email.company}. Navigate to the role to send a follow-up pitch.`);
      return;
    }

    if (action === 'retry') {
      try {
        const token = localStorage.getItem('backoffice_token');
        const res = await fetch(`${API_URL}/api/intelligence/acquisition/pitch-email/scheduled/${email.schedule_id}/retry`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) toast.success(data.message); else toast.error(data.detail || 'Retry failed');
      } catch { toast.error('Retry failed'); }
      loadQueue(); loadStats();
      return;
    }
    if (action === 'cancel') {
      try {
        const token = localStorage.getItem('backoffice_token');
        const res = await fetch(`${API_URL}/api/intelligence/acquisition/pitch-email/scheduled/${email.schedule_id}`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) toast.success('Email cancelled'); else toast.error(data.detail || 'Cancel failed');
      } catch { toast.error('Cancel failed'); }
      loadQueue();
    }
  };

  const markNotificationsRead = async () => {
    try {
      await fetchWithAuth(`${API_URL}/api/intelligence/acquisition/pitch-email/notifications/read`, { method: 'PUT' });
      setUnread(0);
    } catch { /* silent */ }
  };

  const failedCount = (counts.failed || 0);
  const repliedCount = (counts.replied || 0);
  const activeCount = (counts.sent || 0) + (counts.delivered || 0);
  const attentionCount = counts.needs_attention || 0;
  const followUpCount = counts.follow_up_due || 0;
  const pendingApprovalCount = fuCounts.pending_approval || 0;

  return (
    <IntelligenceLayout title="Email Queue" subtitle="Track delivery, replies, and failures" embedded={embedded}>
      <div className="space-y-4">

        {/* Delivery Stats Bar */}
        <div className="grid grid-cols-7 gap-2" data-testid="delivery-stats">
          {[
            { key: 'attention', label: 'Action Needed', val: attentionCount,    color: 'text-orange-600',  bg: 'bg-orange-50',  border: 'border-orange-300' },
            { key: 'pending',   label: 'Pending',       val: counts.pending || 0, color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200' },
            { key: 'sent',      label: 'Sent',          val: (counts.sent || 0) + (counts.delivered || 0), color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
            { key: 'delivered', label: 'Delivered',      val: counts.delivered || 0, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
            { key: 'replied',   label: 'Replied',       val: counts.replied || 0, color: 'text-purple-700',  bg: 'bg-purple-50',  border: 'border-purple-300' },
            { key: 'failed',    label: 'Failed',        val: counts.failed || 0,  color: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-200' },
            { key: 'rate',      label: 'Reply Rate',    val: stats?.reply_rate ? `${stats.reply_rate}%` : '—', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-300' },
          ].map(s => (
            <div key={s.key} className={`p-2.5 ${s.bg} border ${s.border} rounded-xl text-center ${
              s.key === 'replied' || s.key === 'rate' ? 'ring-1 ring-purple-200' :
              s.key === 'attention' && attentionCount > 0 ? 'ring-1 ring-orange-300' : ''
            }`}
              onClick={s.key === 'attention' && attentionCount > 0 ? () => setActiveTab('attention') : undefined}
              style={s.key === 'attention' && attentionCount > 0 ? { cursor: 'pointer' } : {}}>
              <div className={`text-lg font-black ${s.color}`}>{s.val}</div>
              <div className="text-[9px] text-gray-500 font-medium">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Notification Banner */}
        {unread > 0 && (
          <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-xl" data-testid="notification-banner">
            <Bell className="h-4 w-4 text-purple-500 flex-shrink-0" />
            <div className="flex-1">
              {notifications.filter(n => !n.read).slice(0, 3).map((n, i) => (
                <p key={i} className="text-xs text-purple-700">
                  {n.type === 'reply' && <Star className="h-3 w-3 inline text-purple-500 mr-1" />}
                  {n.type === 'bounce' && <AlertTriangle className="h-3 w-3 inline text-red-500 mr-1" />}
                  {n.message}
                </p>
              ))}
            </div>
            <button onClick={markNotificationsRead} className="text-[10px] text-purple-600 hover:text-purple-700 font-medium" data-testid="dismiss-notifications">
              Dismiss
            </button>
          </div>
        )}

        {/* Failed Alert */}
        {failedCount > 0 && activeTab !== 'failed' && (
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-xl cursor-pointer hover:bg-red-100 transition-colors"
            onClick={() => setActiveTab('failed')} data-testid="failed-alert">
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-700 font-medium">{failedCount} email{failedCount > 1 ? 's' : ''} failed — click to review and retry</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {TABS.map(tab => {
            const count = tab.id === 'attention' ? attentionCount :
                          tab.id === 'follow_ups' ? pendingApprovalCount :
                          tab.id === 'active' ? activeCount :
                          tab.id === 'replied' ? repliedCount :
                          counts[tab.id] || 0;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? tab.urgent ? 'border-orange-500 text-orange-700'
                    : tab.highlight ? 'border-purple-500 text-purple-700'
                    : 'border-[#0A1F44] text-[#0A1F44]'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`} data-testid={`queue-tab-${tab.id}`}>
                <tab.icon className={`h-3.5 w-3.5 ${
                  tab.urgent && activeTab === tab.id ? 'text-orange-500' :
                  tab.highlight && activeTab === tab.id ? 'text-purple-500' : ''
                }`} />
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 text-[9px] font-bold rounded-full ${
                    tab.id === 'attention' ? 'bg-orange-100 text-orange-700' :
                    tab.id === 'follow_ups' && count > 0 ? 'bg-blue-100 text-blue-600' :
                    tab.id === 'failed' && count > 0 ? 'bg-red-100 text-red-600' :
                    tab.id === 'replied' ? 'bg-purple-100 text-purple-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
          <div className="flex-1" />
          <button onClick={() => { loadQueue(); loadNotifications(); loadStats(); loadFollowUps(); }} disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-medium text-gray-500 hover:text-[#0A1F44] transition-colors"
            data-testid="refresh-queue">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Follow-Ups Tab Content */}
        {activeTab === 'follow_ups' ? (
          <div className="space-y-3" data-testid="follow-ups-list">
            {followUps.length === 0 ? (
              <div className="text-center py-12">
                <Repeat className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No follow-up sequences</p>
                <p className="text-[10px] text-gray-300 mt-1">Follow-ups are auto-created when you send a pitch email</p>
              </div>
            ) : (
              followUps.map(seq => (
                <SequenceCard key={seq.sequence_id} sequence={seq} onRefresh={() => { loadFollowUps(); loadQueue(); }} />
              ))
            )}
          </div>
        ) : activeTab === 'insights' ? (
          <InsightsTab />
        ) : loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading queue...
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-12">
            {activeTab === 'attention' ? (
              <>
                <CheckCircle className="h-8 w-8 text-emerald-300 mx-auto mb-2" />
                <p className="text-sm text-emerald-600 font-medium">All clear — nothing needs attention</p>
                <p className="text-[10px] text-gray-400 mt-1">No failures, no overdue follow-ups</p>
              </>
            ) : activeTab === 'replied' ? (
              <>
                <MessageSquare className="h-8 w-8 text-purple-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No replies yet</p>
                <p className="text-[10px] text-gray-300 mt-1">Replies appear here when you mark sent emails as replied</p>
              </>
            ) : (
              <>
                <Mail className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No {activeTab === 'active' ? 'sent' : activeTab} emails</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="email-list">
            {emails.map(e => (
              <EmailRow key={e.schedule_id} email={e} onAction={handleAction} />
            ))}
          </div>
        )}

        {/* Status Lifecycle */}
        <div className="flex items-center justify-center gap-1.5 py-3 text-[10px] text-gray-400" data-testid="status-lifecycle">
          <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded font-medium">Pending</span>
          <span>→</span>
          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">Sent</span>
          <span>→</span>
          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded font-medium">Delivered</span>
          <span>→</span>
          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-bold ring-1 ring-purple-200">Replied</span>
          <span className="text-gray-300 mx-1">|</span>
          <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded font-medium">Failed</span>
          <span>→</span>
          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">Retry</span>
        </div>
      </div>

      {/* Modals */}
      {modal?.type === 'reschedule' && <RescheduleModal email={modal.email} onClose={() => setModal(null)} onDone={() => { setModal(null); loadQueue(); }} />}
      {modal?.type === 'edit-time' && <EditTimeModal email={modal.email} onClose={() => setModal(null)} onDone={() => { setModal(null); loadQueue(); }} />}
      {modal?.type === 'reply' && <ReplyModal email={modal.email} onClose={() => setModal(null)} onDone={() => { setModal(null); loadQueue(); loadStats(); loadNotifications(); }} />}
    </IntelligenceLayout>
  );
};

export default ScheduledEmailQueue;
